import {
    Box,
    Typography,
    useTheme,
    CircularProgress
} from '@mui/material';
import React, { useEffect } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import PortalCard from '../../../../../components/PortalCard';
import analyticsService from '../../../../../services/analytics/AnalyticsService';

const PriceRangeDistribution: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState<Array<{
        priceRange: string;
        productCount: number;
        totalValue: number;
    }>>([]);
    const [error, setError] = React.useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await analyticsService.getPriceRangeDistribution();
                if (result.success && result.data) {
                    setData(result.data);
                } else {
                    setError(result.message || 'Failed to fetch price range distribution');
                }
            } catch (err: any) {
                setError(err.message || 'Failed to fetch price range distribution');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const priceRanges = data.map(item => item.priceRange);
    const productCounts = data.map(item => item.productCount);

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    Price Range Distribution
                </Typography>
                <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                    Product count by price range
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 300, width: '100%' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='error' fontFamily='primaryFont'>
                            {error}
                        </Typography>
                    </Box>
                ) : data.length > 0 ? (
                    <BarChart
                        xAxis={[{
                            data: priceRanges,
                            scaleType: 'band',
                            tickLabelStyle: {
                                fontSize: '0.75rem',
                                fill: theme.palette.text.primary,
                                fontWeight: 500,
                                fontFamily: 'primaryFont'
                            }
                        }]}
                        series={[{
                            data: productCounts,
                            label: 'Products',
                            color: theme.palette.info.main,
                        }]}
                        height={350}
                        margin={{ top: 20, right: 30, bottom: 80, left: 60 }}
                        slotProps={{
                            legend: { hidden: true }
                        }}
                        sx={{
                            '& .MuiChartsAxis-tickLabel': {
                                fontSize: '0.75rem',
                                fill: theme.palette.text.secondary,
                                fontFamily: 'primaryFont',
                            },
                            '& .MuiChartsAxis-line': {
                                stroke: theme.palette.divider,
                            },
                            '& .MuiBarElement-root': {
                                rx: 4,
                            },
                        }}
                    />
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant='body2' color='text.secondary' fontFamily='primaryFont'>
                            No data available
                        </Typography>
                    </Box>
                )}
            </Box>
        </PortalCard>
    );
};

export default PriceRangeDistribution;

